// Buying a quantity add-on from the browser: post the form as JSON, run the
// 3-D Secure challenge when the card needs one, ask the server to re-read
// Stripe, and hand back where to go next. Shared by the quantity modal
// (billing--addon-quantity) and the one-click purchases in the limit modal and
// the PSD2 account picker (billing--addon-purchase), so a pending payment,
// a refused purchase and a reply that is not the add-on JSON behave the same
// on every surface.
//
// Every function takes { publishableKey, syncUrl, texts: { genericError,
// networkError }, tag }; `tag` prefixes the console lines ("[addon-quantity]").
// An abort is rethrown for the caller to ignore; any other failure resolves
// { ok: false, error } with the copy to show.
import { loadStripe } from "@stripe/stripe-js"
import { getCsrfToken } from "./csrf.util"
import { isAbortError } from "./fetch-error.util"
import { settlePaymentIntent, stripeLocale } from "./stripe-intent.util"
import { keepStripeFramesOutOfTurboCache } from "./stripe-turbo-cache.util"

// Resolves { ok: true, redirectUrl } once the purchase went through (and any
// payment it needed settled), or { ok: false, error }.
export async function purchaseAddon(form, options) {
  const posted = await postAddonForm(form, options)
  if (!posted.ok) return posted

  const { data } = posted
  if (data.client_secret) {
    const settled = await settleAddonPayment(data, options)
    if (!settled.ok) return settled
    await syncAfterPayment(options.syncUrl, options)
  }
  return { ok: true, redirectUrl: data.redirect_url }
}

// Resolves { ok: true, data } only on `success: true`: a reply that is not the
// add-on JSON (the owner guard's redirect, followed by fetch to an HTML page)
// is an error, never a navigation to an undefined redirect_url.
export async function postAddonForm(form, { texts, tag }) {
  const response = await fetch(form.getAttribute("action"), {
    method: "POST",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    body: new FormData(form)
  }).catch((error) => {
    if (isAbortError(error)) throw error
    console.error(`[${tag}] fetch`, error)
    return null
  })
  if (!response) return { ok: false, error: texts.networkError }

  const data = await response.json().catch(() => null)
  if (response.ok && data?.success === true) return { ok: true, data }

  return { ok: false, error: (data?.errors || []).join(", ") || texts.genericError }
}

export async function settleAddonPayment(data, { publishableKey, texts, tag }) {
  keepStripeFramesOutOfTurboCache()
  const stripe = await loadStripe(publishableKey, { locale: stripeLocale() })
  if (!stripe) {
    console.error(`[${tag}] Stripe.js did not load`)
    return { ok: false, error: texts.genericError }
  }
  const result = await settlePaymentIntent(stripe, {
    clientSecret: data.client_secret,
    status: data.payment_intent_status
  })
  if (result.ok) return { ok: true }

  console.error(`[${tag}] payment intent`, result.reason || result.error)
  return { ok: false, error: result.error || texts.genericError }
}

// The quantity applies once Stripe sees the authenticated payment; ask the
// server to re-read Stripe so the next page does not wait for the webhook.
// A failure here must not keep the customer on a surface they already paid
// from — the webhook converges the row a moment later.
export async function syncAfterPayment(syncUrl, { tag }) {
  if (!syncUrl) return
  await fetch(syncUrl, {
    method: "POST",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() }
  }).catch((error) => console.error(`[${tag}] sync`, error))
}
